import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ShoppingBag, ArrowRight, Download } from 'lucide-react';
import Layout from '../components/Layout';
import { DownloadList } from '../components/DownloadList';
import SourceChips from '../components/SourceChips';
import SEO from '../components/SEO';

var API_URL = process.env.REACT_APP_BACKEND_URL;

function CheckoutSuccessPage() {
  var searchParamsState = useSearchParams();
  var searchParams = searchParamsState[0];
  var sessionId = searchParams.get('session_id');
  
  var statusState = useState('loading');
  var status = statusState[0];
  var setStatus = statusState[1];
  
  var paymentDataState = useState(null);
  var paymentData = paymentDataState[0];
  var setPaymentData = paymentDataState[1];
  
  var attemptsState = useState(0);
  var attempts = attemptsState[0];
  var setAttempts = attemptsState[1];

  var filesState = useState([]);
  var files = filesState[0];
  var setFiles = filesState[1];

  // Only the files from THIS paid order — resolved server-side from the
  // (unguessable) Stripe session id, never from anything the browser chooses.
  useEffect(function() {
    if (status !== 'success' || !sessionId) return;
    fetch(API_URL + '/api/download/session/' + sessionId + '/files')
      .then(function(r) { return r.ok ? r.json() : { files: [] }; })
      .then(function(d) { setFiles(d.files || []); })
      .catch(function() { setFiles([]); });
  }, [status, sessionId]);

  useEffect(function() {
    if (!sessionId) {
      setStatus('no_session');
      return;
    }

    function pollPaymentStatus() {
      if (attempts >= 5) {
        setStatus('timeout');
        return;
      }

      fetch(API_URL + '/api/stripe/status/' + sessionId)
        .then(function(response) {
          if (!response.ok) {
            throw new Error('Failed to check payment status');
          }
          return response.json();
        })
        .then(function(data) {
          setPaymentData(data);
          
          if (data.payment_status === 'paid') {
            setStatus('success');
          } else if (data.status === 'expired') {
            setStatus('expired');
          } else {
            // Continue polling
            setAttempts(function(prev) { return prev + 1; });
            setTimeout(pollPaymentStatus, 2000);
          }
        })
        .catch(function(error) {
          console.error('Error checking payment status:', error);
          setAttempts(function(prev) { return prev + 1; });
          if (attempts < 4) {
            setTimeout(pollPaymentStatus, 2000);
          } else {
            setStatus('error');
          }
        });
    }

    pollPaymentStatus();
  }, [sessionId]);

  function renderContent() {
    if (status === 'loading') {
      return (
        <div className="text-center">
          <Loader2 className="w-16 h-16 mx-auto mb-6 text-[#d4af37] animate-spin" />
          <h1 className="text-3xl font-bold text-white mb-4">Processing Payment</h1>
          <p className="text-neutral-400">Please wait while we confirm your payment...</p>
        </div>
      );
    }

    if (status === 'success') {
      return (
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Payment Successful!</h1>
          <p className="text-neutral-400 mb-8 max-w-md mx-auto">
            Thank you for your purchase. You will receive a confirmation email shortly.
          </p>
          {paymentData && (
            <div className="bg-neutral-900 rounded-xl p-6 mb-8 max-w-sm mx-auto">
              <div className="flex justify-between items-center mb-2">
                <span className="text-neutral-500">Amount Paid</span>
                <span className="text-2xl font-bold text-[#d4af37]">${paymentData.amount_total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-neutral-500">Currency</span>
                <span className="text-white uppercase">{paymentData.currency}</span>
              </div>
            </div>
          )}
          {files.length > 0 ? (
            <div data-testid="success-downloads" className="text-left mb-10">
              <div className="flex items-center gap-2 mb-4 justify-center">
                <Download className="w-4 h-4 text-[#d4af37]" />
                <span className="text-[#d4af37] text-[11px] font-semibold uppercase tracking-[0.18em]">
                  Your downloads
                </span>
              </div>
              <DownloadList files={files} />
              <p className="text-neutral-500 text-xs text-center mt-4">
                Each file allows 5 downloads within 7 days. The same links are in your email, and
                you can always get fresh ones at <Link to="/my-downloads" className="text-[#d4af37]">jimendez.com/my-downloads</Link>.
              </p>
            </div>
          ) : null}
          <div className="text-left">
            <SourceChips orderNumber={(paymentData && paymentData.order_number) || ''}
                         email={(paymentData && paymentData.customer_email) || ''} />
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 bg-[#d4af37] text-black hover:bg-white px-8 py-4 rounded-full font-semibold transition-all"
            >
              <ShoppingBag className="w-5 h-5" />
              Continue Shopping
            </Link>
            <Link
              to="/my-downloads"
              data-testid="success-my-downloads-button"
              className="inline-flex items-center gap-2 border border-[#d4af37]/60 text-[#d4af37] hover:bg-[#d4af37] hover:text-black px-8 py-4 rounded-full font-semibold transition-colors"
            >
              <Download className="w-5 h-5" />
              My Downloads
            </Link>
          </div>
        </div>
      );
    }

    if (status === 'expired' || status === 'timeout') {
      return (
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
            <XCircle className="w-10 h-10 text-yellow-500" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Session Expired</h1>
          <p className="text-neutral-400 mb-8">
            Your payment session has expired. Please try again.
          </p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 bg-[#d4af37] text-black hover:bg-white px-8 py-4 rounded-full font-semibold transition-all"
          >
            Return to Shop
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      );
    }

    if (status === 'no_session' || status === 'error') {
      return (
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
            <XCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Something Went Wrong</h1>
          <p className="text-neutral-400 mb-8">
            We couldn't verify your payment. Please check your email for confirmation or contact support.
          </p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 bg-[#d4af37] text-black hover:bg-white px-8 py-4 rounded-full font-semibold transition-all"
          >
            Return to Shop
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      );
    }

    return null;
  }

  return (
    <Layout>
      <SEO title="Thank You for Your Order" url="/checkout/success" noindex={true} />
      <div className="min-h-screen flex items-center justify-center bg-black py-20">
        <div className="max-w-lg mx-auto px-6">
          {renderContent()}
        </div>
      </div>
    </Layout>
  );
}

export default CheckoutSuccessPage;
